import MenuIcon from '@material-symbols/svg-300/rounded/menu.svg'
import classnames from 'classnames'
import { sortBy } from 'lodash'
import React, { useCallback, useId, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import ProgressBar from '../../components/basics/ProgressBar/ProgressBar'
import { GroupLearningItem } from '../../models/GroupLearningItem'
import Button from '../../components/basics/Button/Button'
import ConfirmDialog from '../../components/basics/ConfirmDialog/ConfirmDialog'
import Menu from '../../components/basics/Menu/Menu'
import LearningItemImage from '../../components/LearningItemImage/LearningItemImage'
import GroupSlug from '../../components/slugs/GroupSlug/GroupSlug'
import { Group } from '../../models/Group'
import {
  userCanAssignSomeLearningItemToGroup,
  userCanEditGroupLearningAssignment,
  userCanRemoveGroupLearningAssignment,
} from '../../state/calculations/permissions/userCan'
import { useGroup, useLearningItem } from '../../state/entities'
import useRemoveLearningAssignmentsFromGroups from '../../state/mutations/useRemoveLearningAssignmentsFromGroups'
import useGroupLearningItems from '../../state/queries/useGroupLearningItems'
import { useSession } from '../../state/session'
import useRoutes from '../../state/useRoutes'
import useDialog from '../../utilities/useDialog'

import styles from './ManageGroupPage.module.scss'
import ChevronRight from '@material-symbols/svg-300/outlined/chevron_right.svg'
import useGroupAssignmentParticipantsDetails, {
  ParticipantDetail,
} from '../../state/queries/useGroupAssignmentParticipantsDetails'
import { filterLearningItemsByKind } from "../../state/calculations/learningItemHelpers";

/**********************************************************************
 * ConfirmDeleteGroupLearningAssignmentDialog
 **********************************************************************/
const ConfirmDeleteGroupLearningAssignmentDialog = React.memo(function ConfirmDeleteGroupLearningAssignmentDialog({
  learningItemUuid,
  groupUuid,
  close,
}: {
  learningItemUuid: string
  groupUuid: string
  close: (result: boolean) => void
}) {
  const { t } = useTranslation()

  const { learningItem } = useLearningItem(learningItemUuid)
  const { group } = useGroup(groupUuid)

  return (
    <ConfirmDialog close={close} yesLabel={t('generic cta.remove')} noLabel={t('generic cta.cancel')}>
      <div>
        <Trans t={t} i18nKey="manage learning.should learning assignment be removed from group">
          <GroupSlug group={group} />
          {{ title: learningItem.title }}
        </Trans>
      </div>
    </ConfirmDialog>
  )
})

/**********************************************************************
 * UnfinishedLearningAssignment
 **********************************************************************/
export const UnfinishedLearningAssignment = React.memo(function UnfinishedLearningAssignment({
  group,
  learningItem,
}: {
  group: Group
  learningItem: GroupLearningItem
}) {
  const { t } = useTranslation()
  const routes = useRoutes()
  const showDialog = useDialog()
  const { user: currentUser } = useSession()
  const [isDetailsVisible, setDetailsVisible] = useState(false)

  const toggleDetails = () => {
    setDetailsVisible(!isDetailsVisible)
  }

  const assignment = useMemo(
    () => sortBy(learningItem.groupLearningAssignments || [], ({ dueDate }) => dueDate || 'z')[0],
    [learningItem]
  )

  // actions
  const actionMenuItems: React.ReactNode[] = []

  // download certificates
  const downloadCertificatesFormId = useId()
  if (learningItem.createCertificate && (learningItem.overallCompletion || 0) > 0) {
    actionMenuItems.push(
      <li key="download-certificates">
        <Button
          onClick={() => (document.getElementById(downloadCertificatesFormId)! as any).submit()}
          data-learning-item-uuid={learningItem.uuid}
          menuItem
        >
          {t('certificate.cta download group certificates')}
        </Button>
      </li>
    )
  }

  // edit assignment
  if (assignment && userCanEditGroupLearningAssignment(currentUser, group)) {
    actionMenuItems.push(
      <li key="edit">
        <Button
          component={Link}
          to={routes.editGroupLearningAssignment.url({
            uuid: group.uuid,
            learningItemUuid: learningItem.uuid,
          })}
          preventScrollReset
          menuItem
        >
          {t('generic cta.edit')}
        </Button>
      </li>
    )
  }

  // remove learning assignment
  const removeLearningAssignmentsFromGroups = useRemoveLearningAssignmentsFromGroups()
  const handleDeleteLearningAssignment = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    async (e) => {
      const learningItemUuid = e.currentTarget.dataset.learningItemUuid
      if (!learningItemUuid) {
        return
      }

      const confirmed = await showDialog(
        (close) => (
          <ConfirmDeleteGroupLearningAssignmentDialog
            close={close}
            learningItemUuid={learningItemUuid}
            groupUuid={group.uuid}
          />
        ),
        () => false
      )
      if (confirmed) {
        removeLearningAssignmentsFromGroups.mutate({ groupUuids: [group.uuid], learningItemUuids: [learningItemUuid] })
      }
    },
    [group, removeLearningAssignmentsFromGroups]
  )
  if (assignment && userCanRemoveGroupLearningAssignment(currentUser, group)) {
    actionMenuItems.push(
      <li key="remove">
        <Button data-learning-item-uuid={learningItem.uuid} onClick={handleDeleteLearningAssignment} menuItem>
          {t('manage learning.cta remove learning assignment')}
        </Button>
      </li>
    )
  }

  return (
    <>
      <section className={styles.assignment}>
        <form
          className={styles.downloadCertificatesForm}
          id={downloadCertificatesFormId}
          method="POST"
          action={`/api/certificate/download-bulk/${group.uuid}`}
        >
          <input type="hidden" name="group_learning_assignments[]" value={assignment.uuid} />
        </form>

        <div className={styles.information}>
          <header>
            <h1>
              <Link to={routes.course.url({ uuid: learningItem.uuid })}>{learningItem.title}</Link>
            </h1>
            <span>{t(`learning item.type.${learningItem.type}`)}</span>
          </header>

          <section>
            {assignment && (
              <>
                <h1>{t('assignment')}</h1>
                <div>
                  <Trans t={t} i18nKey="learning item.assigned {at}">
                    {{ at: assignment.createdAt }}
                  </Trans>
                </div>
              </>
            )}
          </section>

          <section className={styles.progress}>
            <h1>{t('progress')}</h1>
            <div>
              <ProgressBar circular progress={Math.round(learningItem.overallCompletion || 0)} />
            </div>
          </section>
        </div>

        <span onClick={toggleDetails} className={styles.detailsArrow}>
          <ChevronRight className={isDetailsVisible ? styles.open : ''} />
        </span>

        <div className={styles.imageAndDueDate}>
          <Link to={routes.course.url({ uuid: learningItem.uuid })}>
            <LearningItemImage learningItem={learningItem} className={styles.coverImage} />
          </Link>

          <div>
            {assignment?.dueDate && <span>{t('learning item.due date is', { date: assignment.dueDate })}</span>}
          </div>
        </div>

        {actionMenuItems.length > 0 && (
          <Menu small label={<MenuIcon />} className={styles.actions}>
            {actionMenuItems}
          </Menu>
        )}
      </section>

      <div className={isDetailsVisible ? `${styles.details} ${styles.open}` : styles.details}>
        {isDetailsVisible && <ParticipationDetails learningItem={learningItem} group={group} />}
      </div>
    </>
  )
})

function ParticipationDetails({ learningItem, group }: { learningItem: GroupLearningItem; group: Group }) {
  const { t } = useTranslation()
  const participantsDetails = useGroupAssignmentParticipantsDetails(group.uuid, learningItem.uuid)

  return (
    <table className={styles.dateTable}>
      <thead>
        <tr>
          <th className={styles.userColumn}>{t('learning_assignments.participants_details.user column title')}</th>
          <th className={styles.progressColumn}>
            {t('learning_assignments.participants_details.progress column title')}
          </th>
          <th className={styles.progressColumn}>
            {t('learning_assignments.participants_details.status column title')}
          </th>
          <th className={styles.progressColumn}>
            {t('learning_assignments.participants_details.duration column title')}
          </th>
        </tr>
      </thead>
      <tbody>
        {participantsDetails &&
          participantsDetails.map((participantDetail: ParticipantDetail) => {
            return (
              <tr key={participantDetail.userUuid}>
                <td>{participantDetail.fullName}</td>
                <td>{Math.round(participantDetail.completionPercentage || 0)}%</td>
                <td>
                  {participantDetail.status &&
                    t(`learning_assignments.participants_details.status ${participantDetail.status}`)}
                </td>
                <td>{participantDetail.timeInvested}</td>
              </tr>
            )
          })}
        {!participantsDetails || participantsDetails.length === 0 && (
          <tr>
            <td colSpan={4} className={styles.noParticipants}>{t('learning_assignments.participants_details.no participants')}</td>
          </tr>
        )}
      </tbody>
    </table>
  )
}

/**********************************************************************
 * ManageGroupPageLearningAssignments
 **********************************************************************/
export default React.memo(function ManageGroupPageLearningAssignments({ uuid }: { uuid: string }) {
  const { t } = useTranslation()
  const routes = useRoutes()
  const { user: currentUser } = useSession()
  const { group } = useGroup(uuid)

  const { groupLearningItems } = useGroupLearningItems(uuid)

  const groupLearningItemsNotPaths  = useMemo(() => filterLearningItemsByKind(groupLearningItems, ['onDemandCourse', 'scheduledCourse']), [groupLearningItems])

  return (
    <section className={styles.ManageGroupPageLearningAssignments}>
      <header>
        <h1>{t('learning assignments')}</h1>
        {userCanAssignSomeLearningItemToGroup(currentUser, group) && (
          <Button
            small
            component={Link}
            to={routes.createGroupLearningAssignments.url({ uuid, pathsOrCourses: '0' })}
            preventScrollReset
          >
            {t('manage learning.cta assign learning item')}
          </Button>
        )}
      </header>

      <main
        className={classnames(styles.learningAssignmentsTable /*{ skeleton: userFilter.result.isPlaceholderData }*/)}
      >
        {groupLearningItemsNotPaths.map((learningItem) => (
          <UnfinishedLearningAssignment key={learningItem.uuid} group={group} learningItem={learningItem} />
        ))}

        {!groupLearningItemsNotPaths.length && <span className={styles.empty}>{t('no learning assignments found')}</span>}
      </main>
    </section>
  )
})
